import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogOut, CreditCard, Gift, History, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

import {
  MembershipCard,
  RemainingTimeCard,
  BenefitsGrid,
  RedemptionCalendar,
  DiscountRemindersCard,
  ExpirationAlert,
  AnimatedBackground,
  PendingActivationView,
  SavingsSummaryCard,
  DiscountPurchasesHistory,
  RedemptionHistory,
  NotificationBell,
  type Profile,
  type Membership,
  type Redemption,
  type Payment,
  type DiscountPurchase,
} from '@/components/membership';
import villatechBlueLogo from '@/assets/villatech-logo-blue.png';

// Animation variants for staggered entrance
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } }
};

const MiMembresia: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isVendedor, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [discountPurchases, setDiscountPurchases] = useState<DiscountPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (!authLoading && user && (isAdmin || isVendedor)) {
      navigate('/admin/canjes', { replace: true });
      return;
    }

    if (user && !(isAdmin || isVendedor)) {
      loadUserData();
    }
  }, [user, isAdmin, isVendedor, authLoading, navigate]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      // Load profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, nombre, telefono, modelo_celular, username, pin, created_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileData) {
        setProfile(profileData);
      }

      // Load membership
      const { data: membershipData } = await supabase
        .from('memberships')
        .select('id, plan, billing_period, start_date, end_date, is_active, activated_at, activated_by')
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipData) {
        setMembership(membershipData as Membership);

        // Load redemptions for this membership
        const { data: redemptionsData } = await supabase
          .from('benefit_redemptions')
          .select('id, benefit_type, quantity, month_year, notes, created_at, redeemed_by')
          .eq('membership_id', membershipData.id)
          .order('created_at', { ascending: false });

        if (redemptionsData && redemptionsData.length > 0) {
          // Get redeemer profiles for redemptions
          const redeemerIds = [...new Set(redemptionsData.map(r => r.redeemed_by))];
          const { data: redeemerProfiles } = await supabase
            .from('profiles')
            .select('user_id, nombre, ubicacion_tienda')
            .in('user_id', redeemerIds);

          const redeemerMap = new Map(redeemerProfiles?.map(p => [p.user_id, p]) || []);
          
          const redemptionsWithRedeemer = redemptionsData.map(redemption => ({
            ...redemption,
            redeemer_name: redeemerMap.get(redemption.redeemed_by)?.nombre?.split(' ')[0] || undefined,
            redeemer_ubicacion: redeemerMap.get(redemption.redeemed_by)?.ubicacion_tienda || undefined,
          }));
          
          setRedemptions(redemptionsWithRedeemer as Redemption[]);
        } else {
          setRedemptions([]);
        }

        // Load payments for this membership
        const { data: paymentsData } = await supabase
          .from('membership_payments')
          .select('*')
          .eq('membership_id', membershipData.id)
          .order('created_at', { ascending: false });

        if (paymentsData) {
          setPayments(paymentsData as Payment[]);
        }

        // Load discount purchases for this membership with registrar info
        const { data: purchasesData } = await supabase
          .from('discount_purchases')
          .select('*')
          .eq('membership_id', membershipData.id)
          .order('created_at', { ascending: false });

        if (purchasesData && purchasesData.length > 0) {
          // Get registrar profiles for purchases
          const registrarIds = [...new Set(purchasesData.map(p => p.registered_by))];
          const { data: registrarProfiles } = await supabase
            .from('profiles')
            .select('user_id, nombre, ubicacion_tienda')
            .in('user_id', registrarIds);

          const registrarMap = new Map(registrarProfiles?.map(p => [p.user_id, p]) || []);
          
          const purchasesWithRegistrar = purchasesData.map(purchase => ({
            ...purchase,
            registrar_name: registrarMap.get(purchase.registered_by)?.nombre?.split(' ')[0] || undefined,
            registrar_ubicacion: registrarMap.get(purchase.registered_by)?.ubicacion_tienda || undefined,
          }));
          
          setDiscountPurchases(purchasesWithRegistrar as DiscountPurchase[]);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        {/* Background glow effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        </div>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!profile || !membership) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 p-4">
        {/* Background glow effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        </div>
        <div className="text-center relative">
          <p className="text-white/60 mb-4">No se encontró información de membresía</p>
          <Button onClick={handleSignOut} variant="outline" className="text-white border-white/20">
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>
    );
  }

  // Check if membership is pending activation (not active AND never activated)
  const isPendingActivation = membership && !membership.is_active && !membership.activated_at;

  // Tab bar component for portal
  // Mobile Tab Bar (fixed bottom - only on mobile)
  const MobileTabBar = () => (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-slate-900/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom md:hidden">
      <div className="grid grid-cols-4 h-16 max-w-lg mx-auto">
        {[
          { id: 'info', icon: CreditCard, label: 'Info' },
          { id: 'canjes', icon: Gift, label: 'Canjes' },
          { id: 'ahorros', icon: Percent, label: 'Ahorros' },
          { id: 'historial', icon: History, label: 'Historial' },
        ].map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === id 
                ? 'text-blue-400' 
                : 'text-white/50 hover:text-white/70'
            }`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Desktop Tabs (inline - only on desktop)
  const DesktopTabs = () => (
    <div className="hidden md:flex items-center gap-2 bg-white/5 backdrop-blur-xl rounded-2xl p-2 border border-white/10 mb-6">
      {[
        { id: 'info', icon: CreditCard, label: 'Info' },
        { id: 'canjes', icon: Gift, label: 'Canjes' },
        { id: 'ahorros', icon: Percent, label: 'Ahorros' },
        { id: 'historial', icon: History, label: 'Historial' },
      ].map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          onClick={() => setActiveTab(id)}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
            activeTab === id 
              ? 'bg-blue-500 text-white shadow-lg' 
              : 'text-white/60 hover:text-white hover:bg-white/5'
          }`}
        >
          <Icon className="w-5 h-5" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 p-4 pb-24 md:pb-4">
      {/* Background glow effects - same as login */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      </div>
      
      {/* Animated background effects */}
      <AnimatedBackground />

      {/* Mobile Tab Bar via Portal (only on mobile) */}
      {createPortal(<MobileTabBar />, document.body)}

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="relative max-w-4xl mx-auto"
      >
        {/* Glass Header - Redesigned */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-between mb-6 pt-4 pb-4 px-4 -mx-4 bg-white/5 backdrop-blur-xl border-b border-white/10 rounded-t-2xl"
        >
          {/* Left: Notification Bell */}
          <div className="w-10">
            {!isPendingActivation && membership && (
              <NotificationBell
                plan={membership.plan}
                redemptions={redemptions}
                memberName={profile.nombre}
                onViewRedemptions={() => setActiveTab('canjes')}
              />
            )}
          </div>

          {/* Center: Logo */}
          <img src={villatechBlueLogo} alt="VillaTech" className="h-12 md:h-14" />

          {/* Right: Logout Icon */}
          <Button 
            onClick={handleSignOut}
            variant="ghost" 
            size="icon"
            className="w-10 h-10 text-white/60 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </motion.div>

        {/* Welcome message with sparkle animation */}
        <motion.div
          variants={itemVariants}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <motion.span
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="text-2xl"
            >
              ✨
            </motion.span>
            <h1 className="text-xl md:text-2xl font-bold text-white">
              ¡Hola, {profile.nombre.split(' ')[0]}! 👋
            </h1>
          </div>
          <p className="text-zinc-400 text-sm md:text-base ml-10">Bienvenido a tu membresía VillaTech</p>
        </motion.div>

        {/* Pending Activation View - shown at top when pending */}
        {isPendingActivation && (
          <PendingActivationView profile={profile} membership={membership} />
        )}

        {/* Expiration Alert - only for active members */}
        {!isPendingActivation && (
          <ExpirationAlert 
            membership={membership}
            onRenew={() => {
              const message = `¡Hola! Quiero renovar mi membresía ${membership.plan.toUpperCase()} de VillaTech.`;
              window.open(`https://wa.me/50663217695?text=${encodeURIComponent(message)}`, '_blank');
            }}
          />
        )}

        {/* Desktop Tabs */}
        <DesktopTabs />

        {/* Content based on active tab */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <>
              {/* Membership Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-black/80 backdrop-blur-xl rounded-2xl p-4 border border-white/10"
              >
                <MembershipCard profile={profile} membership={membership} isPending={isPendingActivation} />
              </motion.div>

              {/* Remaining months - only for active members */}
              {!isPendingActivation && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <RemainingTimeCard membership={membership} />
                </motion.div>
              )}
            </>
          )}

          {/* Canjes Tab */}
          {activeTab === 'canjes' && (
            <>
              {/* Discount Reminders - moved here */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <DiscountRemindersCard plan={membership.plan} />
              </motion.div>

              {/* Monthly benefits - show for all, but indicate pending state */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={isPendingActivation ? 'opacity-60 pointer-events-none' : ''}
              >
                <BenefitsGrid plan={membership.plan} redemptions={redemptions} layout="grid" />
                {isPendingActivation && (
                  <p className="text-center text-blue-400/70 text-sm mt-2">
                    Disponibles al activar tu membresía
                  </p>
                )}
              </motion.div>

              {/* Redemption Calendar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <RedemptionCalendar 
                  redemptions={redemptions} 
                  plan={membership.plan} 
                  payments={payments}
                  discountPurchases={discountPurchases}
                />
              </motion.div>
            </>
          )}

          {/* Ahorros Tab */}
          {activeTab === 'ahorros' && (
            <>
              {/* Savings Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <SavingsSummaryCard purchases={discountPurchases} plan={membership.plan} />
              </motion.div>

              {/* Discount Purchases History */}
              {discountPurchases.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <DiscountPurchasesHistory purchases={discountPurchases} plan={membership.plan} />
                </motion.div>
              )}
            </>
          )}

          {/* Historial Tab */}
          {activeTab === 'historial' && (
            <>
              {/* Redemption history */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <RedemptionHistory 
                  redemptions={redemptions}
                  plan={membership.plan}
                />
              </motion.div>

              {/* Discount Purchases in History */}
              {discountPurchases.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <DiscountPurchasesHistory purchases={discountPurchases} plan={membership.plan} />
                </motion.div>
              )}
            </>
          )}
        </motion.div>

      </motion.div>
    </div>
  );
};

export default MiMembresia;